import { z } from '@/utils/zod'
import { EntitySchema } from '@/entities/Entity'
import { ExtendedClientAppTypeSchema, LanguageSchema, StorageResourceSchema } from '@/entities/common'
import { TimezoneSchema } from '@/entities/common/date/Timezone'
import { NotificationCategorySchema } from '@/entities/notification'
import { OrganizationAccessSchema } from '@/entities/OrganizationAccess'
import { RoomAccessSchema } from '@/entities/portal/RoomAccess'
import { SystemPermissionsSchema } from '@/entities/SystemPermissions'

export const UserNotificationSettingsSchema = z.object({
    categories: z.partialRecord(
        NotificationCategorySchema,
        z.object({
            email: z.boolean(),
            push: z.boolean(),
        })
    ),
})

export type UserNotificationSettings = z.infer<typeof UserNotificationSettingsSchema>

export const UserLastSeenSchema = z.partialRecord(ExtendedClientAppTypeSchema, z.isoDateTime)

export type UserLastSeen = z.infer<typeof UserLastSeenSchema>

export const getUserLastSeenEntries = (lastSeen: UserLastSeen) =>
    ExtendedClientAppTypeSchema.options
        .flatMap((clientApp) => {
            const seenAt = lastSeen[clientApp]

            return seenAt ? [{ clientApp, seenAt }] : []
        })
        .sort((a, b) => b.seenAt.getTime() - a.seenAt.getTime())

export const InvitationTypeSchema = z.enum(['publicLink']).nullable()

export type InvitationType = z.infer<typeof InvitationTypeSchema>

export const UserOrganizationSchema = OrganizationAccessSchema.extend({
    portal: z.boolean(),

    // -- Contacts claimed by the user for the organization --
    contacts: z.array(
        z.object({
            name: z.string(),
            contact: z.objectId(),
        })
    ),

    // -- Set when someone with system permissions impersonates --
    _impersonatedFromSystemPermissions: z.boolean().optional(),

    // -- Portal stuff --
    roomMemberships: z.array(RoomAccessSchema),
    contactId: z.objectId().nullable(),
    tags: z.array(z.string().min(1)),
    invitationType: InvitationTypeSchema,
})

export type UserOrganization = z.infer<typeof UserOrganizationSchema>

export const UserSchema = EntitySchema.extend({
    userId: z.string().min(16),
    language: LanguageSchema,
    timezone: TimezoneSchema.nullish(),
    avatar: StorageResourceSchema.nullable(),
    name: z.string().min(1).max(50),
    email: z.email(),
    bio: z.string().max(2000),
    notifications: UserNotificationSettingsSchema,
    organizations: z.array(UserOrganizationSchema),
    systemPermissions: SystemPermissionsSchema,
    authProviders: z.array(z.string()),
    lastSeen: UserLastSeenSchema,
})

export type User = z.infer<typeof UserSchema>
